import React, { Fragment, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';
import { GuideProvider } from '../guides/GuideContext';
import GuideOverlay from '../guides/GuideOverlay';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (!user) {
    return null;
  }

  return (
    <GuideProvider>
    <div className="h-screen flex bg-clinic-surface">
      {/* Desktop sidebar — persistent at lg+ */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar userRole={user.role} />
      </div>

      {/* Mobile sidebar — off-canvas drawer below lg */}
      <Transition show={mobileNavOpen} as={Fragment}>
        <Dialog onClose={setMobileNavOpen} className="relative z-50 lg:hidden">
          <TransitionChild
            as={Fragment}
            enter="transition-opacity ease-linear duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-clinic-dark/50" aria-hidden="true" />
          </TransitionChild>
          <div className="fixed inset-0 flex">
            <TransitionChild
              as={Fragment}
              enter="transition ease-in-out duration-200 transform" enterFrom="-translate-x-full" enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative">
                <Sidebar userRole={user.role} onNavigate={() => setMobileNavOpen(false)} />
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header user={user} onOpenNav={() => setMobileNavOpen(true)} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-clinic-surface">
          <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
    <GuideOverlay />
    </GuideProvider>
  );
};

export default DashboardLayout;